export type OutboxOp =
  | "create_animal_case"
  | "create_animal_cases_batch"
  | "submit_care_entry"
  | "attach_media";

export type OutboxStatus =
  | "pending"
  | "synced"
  | "error"
  | "quarantine"
  | "conflict";

export type ConflictKind = "occurrence" | "diagnosis" | "place" | "domain";

export type OutboxItem = {
  id: string;
  op: OutboxOp;
  status: OutboxStatus;
  clientId: string;
  caseId: string | null;
  dependsOn: string[];
  payload: Record<string, unknown>;
  error: string | null;
  conflictKind: ConflictKind | null;
  createdAt: string;
  updatedAt: string;
};

const OP_RANK: Record<OutboxOp, number> = {
  create_animal_case: 0,
  create_animal_cases_batch: 0,
  submit_care_entry: 1,
  attach_media: 2,
};

export function pickReadyOutbox(items: OutboxItem[]): OutboxItem[] {
  const byId = new Map(items.map((item) => [item.id, item]));
  const byClient = new Map(items.map((item) => [item.clientId, item]));

  const resolve = (dep: string): OutboxItem | undefined =>
    byId.get(dep) ?? byClient.get(dep);

  const blockedByFailedDep = (item: OutboxItem, seen = new Set<string>()): boolean => {
    for (const dep of item.dependsOn) {
      if (seen.has(dep)) continue;
      seen.add(dep);
      const other = resolve(dep);
      if (!other) continue;
      if (other.status === "quarantine" || other.status === "conflict") return true;
      if (blockedByFailedDep(other, seen)) return true;
    }
    return false;
  };

  const depsSynced = (item: OutboxItem): boolean => {
    for (const dep of item.dependsOn) {
      const other = resolve(dep);
      if (!other) continue;
      if (other.status !== "synced") return false;
    }
    return true;
  };

  return items
    .filter(
      (item) =>
        (item.status === "pending" || item.status === "error") &&
        depsSynced(item) &&
        !blockedByFailedDep(item)
    )
    .sort((a, b) => {
      const rank = OP_RANK[a.op] - OP_RANK[b.op];
      if (rank !== 0) return rank;
      return a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id);
    });
}
